import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  HostListener,
  inject,
  signal,
  NgZone,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject, Subscription, takeUntil, finalize, expand, map, reduce, EMPTY } from 'rxjs';
import { OrderDetailsModalComponent } from '../components/order-details-modal/order-details-modal.component';
import { AddOrderModalComponent } from '../components/add-order-modal/add-order-modal.component';
import { CancelOrderModalComponent } from '../components/cancel-order-modal/cancel-order-modal.component';
import { OrderCancellationSuccessModalComponent } from '../components/order-cancellation-success-modal/order-cancellation-success-modal.component';
import { OrdersService } from './services/orders.service';
import { LocationService } from '../services/location.service';
import { NotificationService } from '../services/notification.service';
import { RoleService } from '../services/role.service';
import { CancellationService } from '../services/cancellation.service';
import { LoggerService } from '../services/logger.service';
import { OrderUtils } from './utils/order.utils';
import { RealtimeEventsService } from '../services/realtime-events.service';
import { bindPaymentSseEvents } from './utils/payment-realtime-sse.util';
import { bindOrderProcessedSseEvent } from './utils/order-processed-realtime-sse.util';
import {
  handlePaymentFailedFromContext,
  handlePaymentSuccessFromContext,
  PaymentEventContext,
} from './utils/orders-payment-events.util';
import {
  Order,
  OrderStatus,
  OrderStats,
  OrderFilters,
  StatusFilters,
  WashTypeFilters,
} from './models/order.models';
import {
  ORDER_SHIMMER_COLUMNS,
  createDefaultAdminOrderFilters,
  ADMIN_ORDER_FILTER_CATEGORIES,
  ADMIN_ORDER_STATS,
  syncPackageFilterCategoryOptions,
  syncDiscountAppliedFilterCategoryOptions,
  DISCOUNT_APPLIED_FILTER_ALL,
  PaymentModeApiFilterItem,
} from './constants/orders.constants';
import {
  cloneOrderFilters,
  convertCategoriesToOrderFilters as convertSharedCategoriesToOrderFilters,
  isOptionAppliedFromOrderFilters,
  buildPaymentModeFiltersFromOrderFilters,
  buildWashTypeFiltersFromOrderFilters,
  countActiveOrderFilters,
  syncOrderPaymentModeFilterCategories,
  loadOrderPaymentModesForFilter,
  applyPostLoadOrderSort,
} from './utils/orders-page-shared.utils';
import { OrderListTableBase } from './order-list-page.base';
import { PackageService } from '../services/package.service';
import { Package } from '../shared/types';
import { DiscountService } from '../discount/services/discount.service';
import { Discount } from '../discount/models/discount.models';
import { PaginationBarComponent } from '../shared/components/pagination-bar/pagination-bar.component';
import { EmptyResultComponent } from '../shared/components/empty-result/empty-result.component';
import { LoadingTableShimmerComponent } from '../shared/components/loading-table-shimmer/loading-table-shimmer.component';
import { TitleBarComponent } from '../shared/components/title-bar/title-bar.component';
import { StatsCardComponent } from '../shared/components/stats-card/stats-card.component';
import { SearchbarComponent } from '../shared/components/searchbar/searchbar.component';
import { ActionButtonComponent } from '../shared/components/action-button/action-button.component';
import { FilterModalComponent } from '../shared/components/filter-modal/filter-modal.component';
import {
  FilterCategory,
  FilterOption,
} from '../shared/components/filter-modal/filter-modal.component';
import {
  SimpleDropdownComponent,
  DropdownOption,
} from '../shared/components/simple-dropdown/simple-dropdown.component';
import { LocationOption } from '../shared/types';
import {
  DateRangePickerComponent,
  DateRange,
  PeriodOption,
} from '../shared/components/date-range-picker/date-range-picker.component';
import { formatDateToISO, getUserTimezone } from '../shared/utils/date.util';
import {
  EXPORT_CUSTOM_PERIOD,
  EXPORT_PERIOD_OPTIONS,
  resolveExportDateRange,
} from '../shared/utils/export-date-presets.util';
import { notifyDownloadError } from '../shared/utils/download-error.util';
import {
  subscribeToOrdersLprPlateUpdates,
  ordersLprPlateUpdateMarkForCheck,
} from './utils/orders-lpr-plate-update.util';
import {
  ALL_LANES_FILTER_OPTION,
  loadLaneFilterOptions,
  shouldShowLaneFilter,
} from '../shared/utils/lane-filter.util';
import { ApiService } from '../services/api.service';
import { OrdersListTableComponent } from './components/orders-list-table/orders-list-table.component';
import { OrdersActionsDropdownComponent } from './components/orders-actions-dropdown/orders-actions-dropdown.component';
import {
  CancelRefundDone,
  CancelRefundPanelComponent,
} from '../shared/components/cancel-refund-panel/cancel-refund-panel.component';
import { CancelRefundIntent } from '../shared/components/cancel-refund-panel/cancel-refund-panel.logic';
import { CancelRefundService } from '../services/cancel-refund.service';

@Component({
  selector: 'app-admin-orders',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    OrderDetailsModalComponent,
    AddOrderModalComponent,
    CancelOrderModalComponent,
    OrderCancellationSuccessModalComponent,
    PaginationBarComponent,
    EmptyResultComponent,
    LoadingTableShimmerComponent,
    TitleBarComponent,
    StatsCardComponent,
    SearchbarComponent,
    ActionButtonComponent,
    FilterModalComponent,
    SimpleDropdownComponent,
    DateRangePickerComponent,
    OrdersListTableComponent,
    OrdersActionsDropdownComponent,
    CancelRefundPanelComponent,
  ],
  templateUrl: './admin-orders.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./admin-orders.component.scss'],
})
export class AdminOrdersComponent extends OrderListTableBase implements OnInit, OnDestroy {
  @ViewChild(AddOrderModalComponent) addOrderModalComponent!: AddOrderModalComponent;
  @ViewChild(OrderDetailsModalComponent) orderDetailsModalComponent!: OrderDetailsModalComponent;
  currentLocation = '';
  selectedLocationId = '';
  searchTerm = '';
  itemsPerPage = 10;
  currentPage = 1;
  totalItems = 0;
  loading = false;

  // Location dropdown properties
  locationOptions: LocationOption[] = [];
  isLocationDropdownOpen = false;

  // Lane filter (backend `lane_id`; empty = all lanes)
  laneFilterOptions: DropdownOption[] = [ALL_LANES_FILTER_OPTION];
  selectedLaneFilterId = '';
  isLaneDropdownOpen = false;

  // Hide the lane filter when there is at most one selectable lane.
  get showLaneFilter(): boolean {
    return shouldShowLaneFilter(this.laneFilterOptions);
  }

  // CSV export date range
  isCsvDateRangePickerOpen = false;
  isExportingCsv = false;
  csvDateRange: DateRange = { startDate: null, endDate: null };
  csvSelectedPeriod = EXPORT_CUSTOM_PERIOD;
  readonly csvExportPeriodOptions: PeriodOption[] = EXPORT_PERIOD_OPTIONS;

  orderStats: OrderStats[] = ADMIN_ORDER_STATS.map(stat => ({ ...stat, value: '0' }));
  orderStatuses: OrderStatus[] = [];
  orders: Order[] = [];

  showFilterModal = false;

  // Filter categories for shared filter modal (includes Payment Mode multi-select)
  readonly filterCategories = signal<FilterCategory[]>(
    ADMIN_ORDER_FILTER_CATEGORIES.map(category => ({
      ...category,
      options: category.options.map(option => ({ ...option })),
    }))
  );

  // Shimmer loading configuration
  shimmerColumns = ORDER_SHIMMER_COLUMNS;

  appliedFilters: OrderFilters = createDefaultAdminOrderFilters();

  // Current filters for synchronization between quick filters and main filter
  currentFilters: OrderFilters = createDefaultAdminOrderFilters();

  // Dropdown functionality
  openDropdownId: string | null = null;
  selectedOrderForDropdown: Order | null = null;

  // Order details modal
  showOrderDetailsModal = false;
  selectedOrder: Order | null = null;

  // Add order modal
  showAddOrderModal = false;

  // Cancel order modal
  showCancelOrderModal = false;
  orderToCancel: Order | null = null;
  /** CWS-1779: the cancel & refund panel and the row it was opened for. */
  showCancelRefundPanel = false;
  cancelRefundOrder: Order | null = null;
  cancelRefundIntent: CancelRefundIntent = 'cancel';
  /** Cancellation ids whose refund retry is in flight (CWS-1534 review: one retry per row). */
  private readonly retryingRefunds = new Set<string>();

  // Cancellation success modal
  showCancellationSuccessModal = false;
  cancellationRefundAmount = '';
  cancellationWasScheduled = false;

  // Sorting functionality
  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' | 'none' = 'none';

  // Wash type filter functionality

  readonly ordersService = inject(OrdersService);
  private readonly locationService = inject(LocationService);
  private readonly apiService = inject(ApiService);
  private readonly messageService = inject(NotificationService);
  private readonly roleService = inject(RoleService);
  private readonly cancellationService = inject(CancellationService);
  private readonly cancelRefundService = inject(CancelRefundService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly logger = inject(LoggerService);
  private readonly notificationService = inject(NotificationService);
  private readonly ngZone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly realtime = inject(RealtimeEventsService);
  private readonly packageService = inject(PackageService);
  private readonly discountService = inject(DiscountService);
  /** Cached car-wash packages for the Package filter (cascade by Car Wash Type). */
  private filterPackages: Package[] = [];
  /** Cached discount definitions for the Discount Applied filter (normal + custom). */
  private filterDiscounts: Discount[] = [];
  /** Cached payment modes from GET `user/payment-modes` for the Payment Mode filter. */
  private filterPaymentModes: PaymentModeApiFilterItem[] = [];
  /** Admin can always filter terminal-paid orders (not gated on linked readers). */
  private showTerminalPaymentFilter = true;

  // Payment realtime via SSE (`GET /api/v1/events/stream`)
  private paymentSseSub: Subscription | null = null;

  // Background order-processing refresh via SSE (location + lane scoped)
  private orderProcessedSseSub: Subscription | null = null;
  private loadOrdersSub: Subscription | null = null;
  private loadOrderStatsSub: Subscription | null = null;

  // Track processed payments to prevent duplicate toasts
  private processedPayments = new Set<string>();
  private destroy$ = new Subject<void>();
  private queryParamTimeoutIds: ReturnType<typeof setTimeout>[] = [];

  // All features available to admin users

  ngOnInit(): void {
    this.initializeData();
    this.loadLocations();
    this.loadFilterPackages();
    this.loadFilterDiscounts();
    this.loadFilterPaymentModes();
    this.initializePaymentRealtime();
    // order-processed SSE starts after selectedLocationId is set (see loadLocations / selectLocation)

    // Subscribe to refresh events from OrdersService
    this.ordersService.refreshOrders$.pipe(takeUntil(this.destroy$)).subscribe((options: any) => {
      console.log('🔄 Refreshing orders table and stats...');
      if (options?.resetPage) {
        this.currentPage = 1;
      }
      this.loadOrders();
      this.loadOrderStats();
    });

    // Keep Discount Applied options fresh after create/update/delete on the Discount screen.
    this.discountService.discountsCacheCleared$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadFilterDiscounts());

    subscribeToOrdersLprPlateUpdates(
      this.notificationService,
      this.destroy$,
      {
        getOrders: () => this.orders,
        setOrders: orders => {
          this.orders = orders;
        },
        markForCheck: ordersLprPlateUpdateMarkForCheck(this.cdr),
        onOrderPatched: order => {
          if (this.selectedOrder?.id === order.id) {
            this.selectedOrder = order;
          }
        },
      },
      this.ngZone
    );

    // Check for query params to trigger actions
    this.checkQueryParams();
  }

  /** Load all car-wash packages once for the Package filter options. */
  private loadFilterPackages(): void {
    this.packageService
      .getCarWashPackages()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.filterPackages =
            response?.status && Array.isArray(response.data) ? response.data : [];
          this.refreshPackageFilterOptions(this.appliedFilters.packageIds ?? []);
        },
        error: () => {
          this.filterPackages = [];
          this.refreshPackageFilterOptions([]);
        },
      });
  }

  /**
   * Load all discount definitions (Summary + Custom) for the Discount Applied filter.
   * Pages through `package/discounts` (API max 100/page) with no `type` filter.
   */
  private loadFilterDiscounts(): void {
    const pageSize = 100;
    const fetchPage = (page: number) =>
      this.discountService.getDiscounts({
        page,
        limit: pageSize,
        sort_by: 'name',
        order_by: 'asc',
      });

    fetchPage(1)
      .pipe(
        expand(result => {
          const totalPages = result.pagination?.total_pages ?? 1;
          const currentPage = result.pagination?.page ?? 1;
          if (currentPage >= totalPages) {
            return EMPTY;
          }
          return fetchPage(currentPage + 1);
        }),
        map(result => result.rows ?? []),
        reduce((acc: Discount[], rows) => acc.concat(rows), [] as Discount[]),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: rows => {
          this.filterDiscounts = rows;
          this.refreshDiscountAppliedFilterOptions(
            this.appliedFilters.discountApplied ?? DISCOUNT_APPLIED_FILTER_ALL
          );
        },
        error: () => {
          this.filterDiscounts = [];
          this.refreshDiscountAppliedFilterOptions(
            this.appliedFilters.discountApplied ?? DISCOUNT_APPLIED_FILTER_ALL
          );
        },
      });
  }

  /** Load dynamic payment modes once for the Payment Mode filter options. */
  private loadFilterPaymentModes(): void {
    loadOrderPaymentModesForFilter(
      () => this.ordersService.getPaymentModesForFilter(),
      this.destroy$,
      modes => {
        this.filterPaymentModes = modes;
        this.refreshPaymentModeFilterOptions();
      }
    );
  }

  /**
   * Rebuild Package category options from cached packages and current Car Wash Type selection.
   */
  private refreshPackageFilterOptions(selectedPackageIds?: string[]): void {
    this.filterCategories.update(categories =>
      syncPackageFilterCategoryOptions(categories, this.filterPackages, selectedPackageIds)
    );
  }

  /** Rebuild Discount Applied options from cached discounts + current selection. */
  private refreshDiscountAppliedFilterOptions(selectedValue?: string): void {
    this.filterCategories.update(categories =>
      syncDiscountAppliedFilterCategoryOptions(
        categories,
        this.filterDiscounts,
        selectedValue ?? this.appliedFilters.discountApplied ?? DISCOUNT_APPLIED_FILTER_ALL
      )
    );
  }

  /** Rebuild Payment Mode category options from API modes + current selection. */
  private refreshPaymentModeFilterOptions(): void {
    this.filterCategories.update(categories =>
      syncOrderPaymentModeFilterCategories(
        categories,
        this.filterPaymentModes,
        this.appliedFilters.paymentMode,
        this.showTerminalPaymentFilter
      )
    );
  }

  private checkQueryParams(): void {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.get('add') === 'true') {
      this.handleQueryParamAction(() => this.onAddOrder());
    }

    if (urlParams.get('scan') === 'true') {
      this.handleQueryParamAction(() => this.onScanVehicle(), 0);
    }

    const orderId = urlParams.get('orderId');
    if (orderId) {
      this.handleQueryParamAction(() => this.onViewOrderDetails(orderId));
    }
  }

  private handleQueryParamAction(action: () => void, delayMs = 100): void {
    const execute = () => {
      action();
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {},
        replaceUrl: true,
      });
    };

    if (delayMs <= 0) {
      execute();
      return;
    }

    this.queryParamTimeoutIds.push(setTimeout(execute, delayMs));
  }

  onScanVehicle(): void {
    // Implement scan vehicle functionality
    // For now, we'll show a message or navigate to scan page
    console.log('Scan Vehicle action triggered');
    // You can implement the actual scan functionality here
    // For example: this.router.navigate(['/scan-vehicle']);
  }

  private initializeData(): void {
    this.orderStatuses = this.ordersService.getOrderStatuses();
  }

  private loadLocations(): void {
    this.locationService.getLocations().subscribe({
      next: response => {
        if (response.status && response.data) {
          this.locationOptions = this.locationService.getLocationOptions();
          // Set default location if none selected
          if (this.locationOptions.length > 0 && !this.selectedLocationId) {
            this.selectedLocationId = this.locationOptions[0].value;
            this.currentLocation = this.locationOptions[0].label;
            this.loadOrders();
            this.loadOrderStats();
          }
          if (this.selectedLocationId) {
            this.locationService.setShopLocationById(this.selectedLocationId);
            this.loadLaneFilterOptionsForLocation(this.selectedLocationId);
            this.ensureOrderProcessedRealtime();
          }
        }
      },
      error: error => {
        console.error('Error loading locations:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load locations',
        });
      },
    });
  }

  loadOrders(): void {
    this.loadOrdersSub?.unsubscribe();
    this.loading = true;

    // Clear existing data immediately to prevent showing stale data
    this.orders = [];
    this.totalItems = 0;

    const requestedLocationId = this.selectedLocationId;

    // Convert main filter to API format

    const statusFilters = {
      completed: this.appliedFilters.status.completed,
      pending: this.appliedFilters.status.pending,
      cancelled: this.appliedFilters.status.canceled,
      scheduled: this.appliedFilters.status.scheduled,
      inProgress: this.appliedFilters.status.inProgress,
    };

    const washTypeFilters = buildWashTypeFiltersFromOrderFilters(this.appliedFilters);

    const sourceFilters = {
      wayCom: this.appliedFilters.source?.wayCom || false,
      scanAndGo: this.appliedFilters.source?.scanAndGo || false,
      wayApp: this.appliedFilters.source?.wayApp || false,
      website: this.appliedFilters.source?.website || false,
    };

    const paymentModeFilters = buildPaymentModeFiltersFromOrderFilters(this.appliedFilters);

    // Admin orders don't pass period filter - it's only for staff orders
    const request$ = this.ordersService.loadOrders(
      this.currentPage,
      this.itemsPerPage,
      this.searchTerm,
      '', // Legacy filterValue - not used when locationId is provided
      washTypeFilters,
      statusFilters,
      sourceFilters,
      this.selectedLocationId, // Explicit location ID
      undefined, // No period filter for admin orders
      false,
      this.selectedLaneFilterId || undefined,
      paymentModeFilters,
      this.appliedFilters.packageIds ?? [],
      this.appliedFilters.discountApplied
    );

    this.loadOrdersSub = request$.subscribe({
      next: response => {
        if (requestedLocationId !== this.selectedLocationId) {
          return;
        }
        let orders = response.orders;
        this.totalItems = response.totalItems;

        orders = applyPostLoadOrderSort(
          orders,
          this.sortColumn,
          this.sortDirection,
          this.ordersService
        );

        this.orders = orders;

        // Stats are loaded once on initialization, no need to reload on every order update
        this.loading = false;
      },
      error: error => {
        if (requestedLocationId !== this.selectedLocationId) {
          return;
        }
        this.logger.trackError('AdminOrdersComponent.loadOrders', error, {
          page: this.currentPage,
          locationId: this.selectedLocationId,
          searchTerm: this.searchTerm,
        });
        this.loading = false;
      },
    });
  }

  private getAppliedStatusFilters(): StatusFilters {
    return {
      completed: this.appliedFilters.status.completed,
      pending: this.appliedFilters.status.pending,
      cancelled: this.appliedFilters.status.canceled,
      scheduled: this.appliedFilters.status.scheduled,
      inProgress: this.appliedFilters.status.inProgress,
    };
  }

  private getAppliedWashTypeFilters(): WashTypeFilters {
    return buildWashTypeFiltersFromOrderFilters(this.appliedFilters);
  }

  private getAppliedSourceFilters(): {
    wayCom: boolean;
    scanAndGo: boolean;
    wayApp: boolean;
    website: boolean;
  } {
    return {
      wayCom: this.appliedFilters.source?.wayCom || false,
      scanAndGo: this.appliedFilters.source?.scanAndGo || false,
      wayApp: this.appliedFilters.source?.wayApp || false,
      website: this.appliedFilters.source?.website || false,
    };
  }

  toggleCsvDateRangePicker(): void {
    if (this.isExportingCsv) return;
    this.isCsvDateRangePickerOpen = !this.isCsvDateRangePickerOpen;
  }

  closeCsvDateRangePicker(): void {
    this.isCsvDateRangePickerOpen = false;
  }

  onCsvDateRangeApply(data: { period: string; customRange?: DateRange }): void {
    const range = resolveExportDateRange(data.period, data.customRange, getUserTimezone());
    if (!range) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Date Range Required',
        detail: 'Please select both a start date and an end date.',
      });
      return;
    }

    this.csvSelectedPeriod = data.period;
    this.csvDateRange = range;
    this.closeCsvDateRangePicker();
    this.exportFilteredOrdersToCsv();
  }

  private exportFilteredOrdersToCsv(): void {
    const { startDate, endDate } = this.csvDateRange;
    if (!startDate || !endDate || this.isExportingCsv) return;

    const start = formatDateToISO(startDate);
    const end = formatDateToISO(endDate);

    this.isExportingCsv = true;
    this.ordersService
      .downloadOrdersCsv(
        start,
        end,
        this.searchTerm,
        this.getAppliedWashTypeFilters(),
        this.getAppliedStatusFilters(),
        this.getAppliedSourceFilters(),
        this.selectedLocationId || undefined,
        this.selectedLaneFilterId || undefined,
        buildPaymentModeFiltersFromOrderFilters(this.appliedFilters),
        this.appliedFilters.packageIds ?? [],
        this.appliedFilters.discountApplied
      )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isExportingCsv = false;
        })
      )
      .subscribe({
        next: blob => {
          if (!blob?.size) {
            this.messageService.add({
              severity: 'info',
              summary: 'No Orders Found',
              detail: 'There are no orders matching the selected date range and filters.',
            });
            return;
          }

          this.downloadBlob(blob, `orders_${start}_${end}.csv`);
          this.messageService.add({
            severity: 'success',
            summary: 'CSV Downloaded',
            detail: 'Orders CSV downloaded successfully.',
          });
        },
        error: error => {
          this.logger.trackError('AdminOrdersComponent.exportFilteredOrdersToCsv', error, {
            locationId: this.selectedLocationId,
            searchTerm: this.searchTerm,
            startDate: start,
            endDate: end,
          });
          notifyDownloadError(
            error,
            'Unable to download the orders CSV. Please try again.',
            detail =>
              this.messageService.add({ severity: 'error', summary: 'Export Failed', detail })
          );
        },
      });
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
  }

  private loadOrderStats(): void {
    this.loadOrderStatsSub?.unsubscribe();
    const requestedLocationId = this.selectedLocationId;

    this.loadOrderStatsSub = this.ordersService
      .getOrderStatistics(this.selectedLocationId || undefined)
      .subscribe({
        next: stats => {
          if (requestedLocationId !== this.selectedLocationId) {
            return;
          }
          if (this.orderStats.length === stats.length) {
            stats.forEach((newStat, i) => {
              this.orderStats[i].value = newStat.value;
            });
          } else {
            this.orderStats = stats;
          }
        },
        error: error => {
          if (requestedLocationId !== this.selectedLocationId) {
            return;
          }
          console.error('Error loading order statistics:', error);
          // Keep default stats with "0" values on error instead of clearing them
          // This ensures stats cards remain visible even when API fails
        },
      });
  }

  onSearch(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm = target.value;
    this.currentPage = 1;
    this.loadOrders();
  }

  onSearchChange(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.currentPage = 1;
    this.loadOrders();
  }

  onFilter(): void {
    this.showFilterModal = true;
    // Initialize filter categories with current applied state when opening modal
    this.resetFilterCategoriesToAppliedState();
    this.refreshPackageFilterOptions(this.appliedFilters.packageIds ?? []);
    this.refreshDiscountAppliedFilterOptions(
      this.appliedFilters.discountApplied ?? DISCOUNT_APPLIED_FILTER_ALL
    );
  }

  onFilterModalClose(): void {
    this.showFilterModal = false;
  }

  private getDefaultFilters(): OrderFilters {
    return createDefaultAdminOrderFilters();
  }

  private deepCopyFilters(filters: OrderFilters): OrderFilters {
    return cloneOrderFilters(filters);
  }

  onFilterModalApply(filters: OrderFilters): void {
    this.appliedFilters = this.deepCopyFilters(filters);
    this.currentPage = 1;
    this.showFilterModal = false;

    // Main filter - call API to fetch filtered data
    this.loadOrders();
  }

  // New shared filter modal methods
  onFilterModalApplyShared(categories: FilterCategory[]): void {
    // Convert shared filter categories to OrderFilters format
    const filters = this.convertCategoriesToOrderFilters(categories);
    this.onFilterModalApply(filters);
  }

  onCloseFilterModal(): void {
    this.showFilterModal = false;
    // Reset checkboxes to match current applied filters when closing without applying
    this.resetFilterCategoriesToAppliedState();
    this.refreshPackageFilterOptions(this.appliedFilters.packageIds ?? []);
    this.refreshDiscountAppliedFilterOptions(
      this.appliedFilters.discountApplied ?? DISCOUNT_APPLIED_FILTER_ALL
    );
  }

  /** Cascade Package options when Car Wash Type checkboxes change. */
  onFilterOptionChange(event: { categoryId: string }): void {
    if (event.categoryId !== 'carWashType') {
      return;
    }
    this.refreshPackageFilterOptions();
  }

  private convertCategoriesToOrderFilters(categories: FilterCategory[]): OrderFilters {
    return convertSharedCategoriesToOrderFilters(categories, this.getDefaultFilters());
  }

  private resetFilterCategoriesToAppliedState(): void {
    this.filterCategories.update((categories: FilterCategory[]) =>
      categories.map((category: FilterCategory) => ({
        ...category,
        options: category.options.map((option: FilterOption) => ({
          ...option,
          selected: this.isOptionApplied(category.id, option.value),
        })),
      }))
    );
    this.refreshPackageFilterOptions(this.appliedFilters.packageIds ?? []);
    this.refreshDiscountAppliedFilterOptions(
      this.appliedFilters.discountApplied ?? DISCOUNT_APPLIED_FILTER_ALL
    );
    this.refreshPaymentModeFilterOptions();
  }

  private isOptionApplied(categoryId: string, optionValue: string): boolean {
    return isOptionAppliedFromOrderFilters(this.appliedFilters, categoryId, optionValue);
  }

  get hasActiveFilters(): boolean {
    return this.getActiveFilterCount() > 0;
  }

  get hasSearchOrFilters(): boolean {
    return (
      (!!this.searchTerm && this.searchTerm.trim().length > 0) ||
      this.hasActiveFilters ||
      (!!this.selectedLocationId && this.selectedLocationId !== '')
    );
  }

  get hasMainFiltersSelected(): boolean {
    return this.getSelectedFilterCount() > 0;
  }

  get hasFilterChanges(): boolean {
    return this.hasMainFiltersSelected || this.hasActiveFilters;
  }

  private getSelectedFilterCount(): number {
    return countActiveOrderFilters(this.appliedFilters);
  }

  getActiveFilterCount(): number {
    return countActiveOrderFilters(this.appliedFilters);
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.currentPage = 1;
    this.loadOrders();
  }

  toggleLocationDropdown(): void {
    this.isLocationDropdownOpen = !this.isLocationDropdownOpen;
    if (this.isLocationDropdownOpen) {
      this.isLaneDropdownOpen = false;
    }
  }

  closeLocationDropdown(): void {
    this.isLocationDropdownOpen = false;
  }

  selectLocation(locationId: string): void {
    this.selectedLocationId = locationId || '';
    if (!locationId) {
      this.currentLocation = this.locationOptions.length > 0 ? '' : 'No Location';
    } else {
      const match = this.locationOptions.find(opt => opt.value === locationId);
      this.currentLocation = match ? match.label : locationId;
    }
    this.isLocationDropdownOpen = false; // Close dropdown after selection
    this.currentPage = 1; // Reset to first page
    this.selectedLaneFilterId = '';
    this.isLaneDropdownOpen = false;
    this.locationService.setShopLocationById(this.selectedLocationId);
    this.loadLaneFilterOptionsForLocation(this.selectedLocationId);
    this.ensureOrderProcessedRealtime();
    this.loadOrders(); // Reload orders with new location filter
    this.loadOrderStats();
  }

  toggleLaneDropdown(): void {
    this.isLaneDropdownOpen = !this.isLaneDropdownOpen;
    if (this.isLaneDropdownOpen) {
      this.isLocationDropdownOpen = false;
    }
  }

  closeLaneDropdown(): void {
    this.isLaneDropdownOpen = false;
  }

  selectLaneFilter(laneId: string): void {
    this.selectedLaneFilterId = laneId || '';
    this.isLaneDropdownOpen = false;
    this.currentPage = 1;
    this.loadOrders();
  }

  private loadLaneFilterOptionsForLocation(locationId: string): void {
    loadLaneFilterOptions(this.apiService, locationId).subscribe({
      next: options => {
        // Ignore stale responses if the location changed while this request was in flight.
        if (locationId !== this.selectedLocationId) {
          return;
        }
        this.laneFilterOptions = options;
        if (
          this.selectedLaneFilterId &&
          !options.some(option => option.value === this.selectedLaneFilterId)
        ) {
          this.selectedLaneFilterId = '';
        }
      },
      error: () => {
        if (locationId !== this.selectedLocationId) {
          return;
        }
        this.laneFilterOptions = [ALL_LANES_FILTER_OPTION];
      },
    });
  }

  onAddOrder(): void {
    console.log('onAddOrder called - opening modal');
    this.showAddOrderModal = true;
  }

  get onAddOrderClick() {
    return () => this.onAddOrder();
  }

  onCloseAddOrder(): void {
    this.showAddOrderModal = false;
  }

  onOrderCreated(): void {
    this.logger.trackOrder('Order created — refreshing table and stats');
    this.loadOrders();
    this.loadOrderStats();
  }

  onViewOrderDetails(orderId: string): void {
    this.showAddOrderModal = false;
    this.loadOrders();
    this.loadOrderStats();
    this.ordersService
      .getOrderById(orderId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (order: Order) => {
          this.selectedOrder = order;
          this.showOrderDetailsModal = true;
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load order details. Please try again.',
          });
        },
      });
  }

  onContinueAddOrder(_payload: { tierId: string; optionId: string }): void {
    // For now, just close modal and keep placeholder for future steps
    this.showAddOrderModal = false;
  }

  // Dropdown methods
  toggleDropdown(event: Event, orderId: string): void {
    event.stopPropagation();

    if (this.openDropdownId === orderId) {
      this.closeDropdown();
    } else {
      this.openDropdown(event, orderId);
    }
  }

  private closeDropdown(): void {
    this.openDropdownId = null;
    this.selectedOrderForDropdown = null;
  }

  private openDropdown(event: Event, orderId: string): void {
    this.openDropdownId = orderId;
    this.selectedOrderForDropdown = this.orders.find(order => order.id === orderId) || null;

    // Position the dropdown correctly
    setTimeout(() => {
      const button = event.target as HTMLElement;
      const dropdown = document.querySelector('.actions-dropdown-menu') as HTMLElement;
      if (button && dropdown) {
        const rect = button.getBoundingClientRect();
        dropdown.style.top = `${rect.bottom + 5}px`;
        dropdown.style.left = `${rect.right - dropdown.offsetWidth}px`;
      }
    }, 0);
  }

  viewOrder(order: Order): void {
    this.closeDropdown();
    this.selectedOrder = order;
    this.showOrderDetailsModal = true;
  }

  /**
   * CWS-1779: where the server has decided the row (feature on at this shop),
   * Cancel opens the cancel & refund panel. Otherwise today's cancel dialog.
   */
  cancelOrder(order: Order): void {
    this.closeDropdown();
    if (order.cancellation?.available === true) {
      this.openCancelRefund(order, 'cancel');
      return;
    }
    this.orderToCancel = order;
    this.showCancelOrderModal = true;
  }

  /** CWS-1779: Refund on a completed item opens the cancel & refund panel (CWS-1778). */
  refundOrder(order: Order): void {
    this.closeDropdown();
    this.openCancelRefund(order, 'refund');
  }

  private openCancelRefund(order: Order, intent: CancelRefundIntent): void {
    this.cancelRefundOrder = order;
    this.cancelRefundIntent = intent;
    this.showCancelRefundPanel = true;
  }

  /** The order's own location; the page's scope when the row did not carry one. */
  get cancelRefundLocationId(): string {
    return this.cancelRefundOrder?.locationId || this.selectedLocationId || '';
  }

  closeCancelRefundPanel(): void {
    this.showCancelRefundPanel = false;
    this.cancelRefundOrder = null;
  }

  /**
   * CWS-1947: the panel was closed without actioning anything.
   *
   * Reload the rows anyway. The row that offered Cancel is a snapshot from
   * whenever the list last loaded, and the usual reason an operator ends up
   * here is that it had gone stale -- the order auto-completed in the
   * background, the badge still read In Progress, and the panel had to tell
   * them the order can only be refunded now. Leaving the same stale row on
   * screen would send them straight back into it.
   */
  onCancelRefundClosed(): void {
    this.closeCancelRefundPanel();
    this.loadOrders();
  }

  /**
   * The panel executed a cancellation. As in the design, the modal closes and
   * the operator reads what happened in a toast; the row's status, flags and
   * the stat cards all moved, so the list reloads.
   */
  onCancelRefundDone(done?: CancelRefundDone): void {
    this.closeCancelRefundPanel();
    if (done) {
      const failed = done.result.status === 'refund_failed' || done.result.status === 'failed';
      this.messageService.add({
        severity: failed ? 'warn' : 'success',
        summary: failed
          ? 'Refund not sent'
          : done.result.cancellation?.action === 'refund'
            ? 'Refunded'
            : 'Cancelled',
        detail: done.summary,
      });
    }
    this.loadOrders();
    this.loadOrderStats();
  }

  /**
   * CWS-1779: the row's last cancellation still owes a refund. Retry it in
   * place; the cancellation itself is already done, so there is nothing to
   * ask the operator again.
   */
  retryRefund(order: Order): void {
    this.closeDropdown();
    const cancellationId = order.cancellation?.refund_failed?.cancellation_id;
    if (!cancellationId) return;
    // One retry per row at a time: the request takes a Stripe round trip, the row keeps its
    // badge until the list reloads, and a second click meanwhile must not reach the server.
    if (this.retryingRefunds.has(cancellationId)) {
      this.messageService.add({
        severity: 'info',
        summary: 'Retrying refund',
        detail: `The refund on ${order.orderId} is already being retried.`,
      });
      return;
    }
    this.retryingRefunds.add(cancellationId);
    this.cancelRefundService
      .retryRefund(cancellationId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.retryingRefunds.delete(cancellationId))
      )
      .subscribe({
        next: res => {
          if (res.status && res.data && res.data.status !== 'refund_failed') {
            this.messageService.add({
              severity: 'success',
              summary: 'Refund sent',
              detail: `The refund on ${order.orderId} has gone out.`,
            });
            this.onCancelRefundDone();
            return;
          }
          this.messageService.add({
            severity: 'error',
            summary: 'Refund failed again',
            detail:
              res.message || `The refund on ${order.orderId} could not be sent. Try again later.`,
          });
        },
        error: err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Refund failed again',
            detail: err?.error?.message || `The refund on ${order.orderId} could not be sent.`,
          });
        },
      });
  }

  onCancelOrderConfirm(data: { order: Order; reason: string; action?: string }): void {
    if (data.action === 'book-again') {
      this.showAddOrderModal = true;
    } else {
      this.logger.trackOrder('Order cancellation requested', {
        orderId: data.order.orderId,
        orderStatus: data.order.status?.value,
        reason: data.reason,
      });

      this.cancellationService.cancelOrder(data.order.id, data.reason).subscribe({
        next: response => {
          if (response.status) {
            const wasScheduledOrder = data.order?.status?.value === 'scheduled';
            const orderPrice = data.order?.price || '';

            this.logger.trackOrder('Order cancelled successfully', {
              orderId: data.order.orderId,
              wasScheduled: wasScheduledOrder,
              refundAmount: orderPrice,
            });

            this.cancellationWasScheduled = wasScheduledOrder;
            this.cancellationRefundAmount = orderPrice;
            this.showCancellationSuccessModal = true;

            this.loadOrders();
            this.loadOrderStats();
          } else {
            this.logger.warn(
              'Order cancellation API returned failure status',
              { orderId: data.order.orderId },
              'AdminOrdersComponent'
            );
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to cancel order. Please try again.',
            });
          }
        },
        error: error => {
          this.logger.trackError('AdminOrdersComponent.cancelOrder', error, {
            orderId: data.order.orderId,
            reason: data.reason,
          });
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to cancel order. Please try again.',
          });
        },
      });
    }

    this.closeCancelOrderModal();
  }

  closeCancelOrderModal(): void {
    this.showCancelOrderModal = false;
    this.orderToCancel = null;
  }

  // Close dropdown when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;

    if (!target.closest('.actions')) {
      this.closeDropdown();
    }

    if (!target.closest('.csv-export-wrapper') && this.isCsvDateRangePickerOpen) {
      this.closeCsvDateRangePicker();
    }
  }

  // Sorting functionality — inherited from OrderListTableBase

  // Generic filter methods
  private openGenericFilterModal<T>(
    event: Event,
    headerSelector: string,
    modalSelector: string,
    showModalProperty: keyof this,
    filtersProperty: keyof this,
    appliedFiltersProperty: keyof this
  ): void {
    (this as any)[filtersProperty] = { ...(this as any)[appliedFiltersProperty] };
    (this as any)[showModalProperty] = true;
    this.positionModal(event, headerSelector, modalSelector);
  }

  private closeGenericFilterModal<T>(
    showModalProperty: keyof this,
    filtersProperty: keyof this,
    appliedFiltersProperty: keyof this
  ): void {
    (this as any)[showModalProperty] = false;
    (this as any)[filtersProperty] = { ...(this as any)[appliedFiltersProperty] };
  }

  private clearGenericFilters<T>(filtersProperty: keyof this, getDefaultMethod: () => T): void {
    (this as any)[filtersProperty] = getDefaultMethod();
  }

  private applyGenericFilters<T>(
    filtersProperty: keyof this,
    appliedFiltersProperty: keyof this,
    showModalProperty: keyof this
  ): void {
    (this as any)[appliedFiltersProperty] = { ...(this as any)[filtersProperty] };
    (this as any)[showModalProperty] = false;
    this.currentPage = 1;
  }

  private hasSelectedFilters<T>(filtersProperty: keyof this): boolean {
    return Object.values((this as any)[filtersProperty]).some((filter: any) => filter);
  }

  private hasAppliedFilters<T>(appliedFiltersProperty: keyof this): boolean {
    return Object.values((this as any)[appliedFiltersProperty]).some((filter: any) => filter);
  }

  private positionModal(event: Event, headerSelector: string, modalSelector: string): void {
    setTimeout(() => {
      const header = (event.target as HTMLElement).closest(headerSelector) as HTMLElement;
      const modal = document.querySelector(modalSelector) as HTMLElement;

      if (header && modal) {
        const rect = header.getBoundingClientRect();
        const modalWidth = 240; // Fixed width from CSS

        // Find the filter icon within the header (works for all filter types)
        const filterIcon = header.querySelector('.filter-icon') as HTMLElement;
        let iconLeft = rect.left;

        if (filterIcon) {
          const iconRect = filterIcon.getBoundingClientRect();
          iconLeft = iconRect.left;
        }

        modal.style.top = `${rect.bottom + 5}px`;
        modal.style.left = `${iconLeft}px`;
      }
    }, 0);
  }

  // Order details modal methods
  openOrderDetails(order: Order): void {
    this.selectedOrder = order;
    this.showOrderDetailsModal = true;
  }

  closeOrderDetails(): void {
    this.showOrderDetailsModal = false;
    this.selectedOrder = null;
  }

  onOrderCancelled(_cancelledOrder: Order): void {
    this.loadOrders();
    this.closeOrderDetails();
  }

  onBookAgainFromOrderDetails(): void {
    // Open the add order modal
    this.showAddOrderModal = true;
  }

  onCancellationSuccessClose(): void {
    this.showCancellationSuccessModal = false;
  }

  onCancellationBookAgain(): void {
    // Close the cancellation success modal
    this.showCancellationSuccessModal = false;
    // Open the add order modal
    this.showAddOrderModal = true;
  }

  canCancelOrder(order: Order): boolean {
    return OrderUtils.canBeCancelled(order);
  }

  canShowActions(_order: Order): boolean {
    return true;
  }

  ngOnDestroy(): void {
    this.queryParamTimeoutIds.forEach(id => clearTimeout(id));
    this.queryParamTimeoutIds = [];

    // Clean up subscriptions
    this.destroy$.next();
    this.destroy$.complete();

    // Clean up payment realtime
    this.disconnectPaymentRealtime();
    this.disconnectOrderProcessedRealtime();
    this.loadOrdersSub?.unsubscribe();
    this.loadOrdersSub = null;
    this.loadOrderStatsSub?.unsubscribe();
    this.loadOrderStatsSub = null;
  }

  /**
   * Background order-processing refresh via SSE (`GET /api/v1/events/stream`).
   * Starts only after a concrete selectedLocationId is available so early events
   * cannot refresh with an empty location filter.
   */
  private ensureOrderProcessedRealtime(): void {
    if (this.orderProcessedSseSub || !String(this.selectedLocationId || '').trim()) {
      return;
    }
    this.orderProcessedSseSub = bindOrderProcessedSseEvent(this.realtime, this.ngZone, {
      getLocationId: () => this.selectedLocationId,
      getSelectedLaneId: () => this.selectedLaneFilterId,
      onOrderProcessed: data => {
        this.logger.trackOrder('SSE order processed — refreshing table and stats', {
          data,
          locationId: this.selectedLocationId,
          laneFilterId: this.selectedLaneFilterId || null,
        });
        this.loadOrders();
        this.loadOrderStats();
      },
    });
  }

  private disconnectOrderProcessedRealtime(): void {
    this.orderProcessedSseSub?.unsubscribe();
    this.orderProcessedSseSub = null;
  }

  /**
   * Payment realtime via SSE (`GET /api/v1/events/stream`).
   * Channel on the SSE envelope is metadata only; stream is multiplexed.
   */
  private initializePaymentRealtime(): void {
    this.paymentSseSub = bindPaymentSseEvents(this.realtime, this.ngZone, {
      onSuccess: (eventName, data) => {
        this.logger.trackPayment('SSE payment success', { eventName, data });
        this.handlePaymentSuccess(eventName, data);
      },
      onFailed: (eventName, data) => {
        this.logger.trackPayment('SSE payment failed', { eventName, data });
        this.handlePaymentFailed(eventName, data);
      },
      skipNamedInCatchAll: true,
    });
  }

  private disconnectPaymentRealtime(): void {
    this.paymentSseSub?.unsubscribe();
    this.paymentSseSub = null;
    this.processedPayments.clear();
  }

  /**
   * Handle payment success event and refresh orders
   */
  private handlePaymentSuccess(eventName: string, data: any): void {
    handlePaymentSuccessFromContext(this.getPaymentEventContext(), eventName, data);
  }

  /**
   * Handle payment failure event
   */
  private handlePaymentFailed(eventName: string, data: any): void {
    handlePaymentFailedFromContext(this.getPaymentEventContext(), eventName, data);
  }

  private getPaymentEventContext(): PaymentEventContext {
    return {
      processedPayments: this.processedPayments,
      messageService: this.messageService,
      closePaymentLinkModal: () => this.addOrderModalComponent?.closePaymentLinkModal(),
      closeAddOrderModal: () => {
        this.showAddOrderModal = false;
      },
      emitGlobalPaymentSucceeded: orderId => this.ordersService.emitPaymentSucceeded(orderId),
      loadOrders: () => this.loadOrders(),
      showOrderDetailsModal: this.showOrderDetailsModal,
      orderDetailsModalComponent: this.orderDetailsModalComponent,
      selectedOrder: this.selectedOrder,
      logger: this.logger,
    };
  }
}
